import React from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon } from "lucide-react";
const CalendarBlank = CalendarIcon;
import { HOMESTAY } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InquiryForm() {
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    guests: "2",
    message: "",
  });
  const [range, setRange] = React.useState(undefined);
  const [submitting, setSubmitting] = React.useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        guests: parseInt(form.guests, 10) || 2,
        message: form.message.trim(),
        check_in: range?.from ? format(range.from, "yyyy-MM-dd") : null,
        check_out: range?.to ? format(range.to, "yyyy-MM-dd") : null,
      };
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Thanks! We'll be in touch within a few hours.");
      setForm({ name: "", email: "", phone: "", guests: "2", message: "" });
      setRange(undefined);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please WhatsApp us.";
      toast.error(typeof msg === "string" ? msg : "Failed to send. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = range?.from
    ? range.to
      ? `${format(range.from, "MMM d")} — ${format(range.to, "MMM d")}`
      : `${format(range.from, "MMM d")} — select end`
    : "Select dates";

  return (
    <section id="inquiry" data-testid="inquiry-section" className="bg-mrn-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-mrn-terracotta font-medium">
            Plan your stay
          </p>
          <h2 className="mt-4 headline-display text-4xl md:text-6xl text-mrn-wood">
            Tell us your <em className="font-light text-mrn-green">weekend plan.</em>
          </h2>
          <p className="mt-6 text-mrn-wood-soft max-w-md">
            Send a quick note and we’ll reply with availability, pricing details
            and a tailored plan — usually within a few hours.
          </p>

          <div className="mt-10 space-y-4 text-mrn-wood">
            <a
              href={`tel:${HOMESTAY.contact.phone}`}
              data-testid="inquiry-phone-link"
              className="flex items-center gap-3 text-lg font-medium link-underline w-fit"
            >
              📞 {HOMESTAY.contact.phoneDisplay}
            </a>
            <a
              href={`mailto:${HOMESTAY.contact.email}`}
              data-testid="inquiry-email-link"
              className="flex items-center gap-3 text-base font-medium link-underline w-fit break-all"
            >
              ✉️ {HOMESTAY.contact.email}
            </a>
            <a
              href={`https://wa.me/${HOMESTAY.contact.whatsapp}?text=${encodeURIComponent("Hi, I'd like to enquire about Mrinmaya vacation home.")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="inquiry-whatsapp-link"
              className="mrn-pill mrn-pill-outline"
            >
              WhatsApp us
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="inquiry-form"
          className="lg:col-span-7 bg-white rounded-2xl border border-mrn-sand-dark/30 p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(59,42,32,0.25)]"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Your name">
              <input
                required
                data-testid="input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mrn-input"
                placeholder="Jane Doe"
              />
            </Field>
            <Field label="Email">
              <input
                required
                type="email"
                data-testid="input-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mrn-input"
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Phone">
              <input
                required
                type="tel"
                data-testid="input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="mrn-input"
                placeholder="+91 98XXXXXXXX"
              />
            </Field>
            <Field label="Guests">
              <Select value={form.guests} onValueChange={(v) => update("guests", v)}>
                <SelectTrigger data-testid="select-guests" className="mrn-input !h-auto py-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {i + 1} guest{i === 0 ? "" : "s"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="md:col-span-2">
              <Field label="Dates">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="dates-trigger"
                      className="mrn-input flex items-center justify-between w-full text-left"
                    >
                      <span className={range?.from ? "text-mrn-wood" : "text-mrn-wood-soft"}>
                        {dateLabel}
                      </span>
                      <CalendarBlank size={18} className="text-mrn-terracotta" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="range"
                      selected={range}
                      onSelect={setRange}
                      numberOfMonths={2}
                      disabled={{ before: new Date() }}
                      data-testid="calendar-range"
                    />
                  </PopoverContent>
                </Popover>
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field label="Anything we should know?">
                <textarea
                  rows={4}
                  data-testid="input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="mrn-input resize-none"
                  placeholder="Birthday, office offsite, IPL screening, pet-friendly questions…"
                />
              </Field>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inquiry-submit"
            className="mrn-pill mrn-pill-primary mt-8 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send inquiry"}
          </button>
        </form>
      </div>

      <style>{`
        .mrn-input {
          width: 100%;
          border: 1px solid rgba(200,185,146,0.6);
          background: #FBF8F2;
          border-radius: 0.75rem;
          padding: 0.75rem 0.9rem;
          color: #3B2A20;
          font-family: 'Outfit', sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .mrn-input::placeholder { color: #8a7a6f; }
        .mrn-input:focus {
          border-color: #C85A3D;
          box-shadow: 0 0 0 3px rgba(200,90,61,0.15);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-mrn-wood-soft font-medium">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
